// Geometry and arithmetic for the transfer quantity picker -- the small panel that Take and Give
// open inside the chest/trade screen so a stack can be split without leaving the mouse. Pure
// numbers, no pixi: the widget that draws them is modern_inventory_dialog.ts, and every row below is
// checked in __tests__/quantity_picker.test.ts.
//
// The picker has three ways to land on an amount, and they all write the same value: drag the slider,
// tap a chip (1 / Half / All), or just type the number. Typing keeps its own digit BUFFER rather than
// a number, because a half-typed "1" on the way to "12" has to show as "1" without the slider
// snapping to 1 and back.

export const PICKER_W = 264
export const PICKER_PAD = 14
// Inner content width -- the Cancel/confirm row spans exactly this.
export const PICKER_INNER_W = PICKER_W - PICKER_PAD * 2

export const TITLE_Y = 11
export const TITLE_SIZE = 13

// The amount readout: a boxed number, with "/ max" alongside it. Boxed because the box is what says
// "you can type in here".
export const READOUT_Y = 32
export const READOUT_W = 86
export const READOUT_H = 30
export const READOUT_GAP = 8
export const READOUT_SIZE = 20

// Stepper row: [-] [======= track =======] [+], the steppers pinned to the margins.
export const STEP_Y = 76
export const STEP_SIZE = 24
export const TRACK_GAP = 10
export const TRACK_X = PICKER_PAD + STEP_SIZE + TRACK_GAP
export const TRACK_W = PICKER_W - TRACK_X * 2
export const TRACK_H = 6
export const TRACK_CENTER_Y = STEP_Y + STEP_SIZE / 2
export const HANDLE_R = 8

// Quick chips, centered as a row.
export const CHIP_Y = 108
export const CHIP_W = 64
export const CHIP_H = 22
export const CHIP_GAP = 8
export const CHIP_ROW_X = (PICKER_W - (CHIP_W * 3 + CHIP_GAP * 2)) / 2

// Cancel + confirm. The confirm button carries the amount too ("Take 12"), so the number is readable
// from wherever the pointer happens to be.
export const ACTION_Y = 140
export const ACTION_H = 30
export const CANCEL_W = 100
export const ACTION_GAP = 12
export const CONFIRM_X = PICKER_PAD + CANCEL_W + ACTION_GAP
export const CONFIRM_W = PICKER_INNER_W - CANCEL_W - ACTION_GAP

export const PICKER_H = ACTION_Y + ACTION_H + PICKER_PAD

// Where the picker sits inside the docked panel: centered, but never off the top of a short panel.
export const pickerOrigin = (panelW: number, panelH: number): { x: number; y: number } => ({
    x: Math.max(0, Math.round((panelW - PICKER_W) / 2)),
    y: Math.max(8, Math.round((panelH - PICKER_H) / 2)),
})

export const clampQuantity = (value: number, max: number): number => {
    if (!Number.isFinite(value)) {
        return 1
    }
    return Math.max(1, Math.min(Math.floor(value), Math.max(1, Math.floor(max))))
}

// Slider mapping. 1 sits at the left end of the track and max at the right, so a single-item stack
// (which never opens the picker) degenerates to a pinned handle rather than a divide by zero.
export const offsetForQuantity = (quantity: number, max: number, trackW: number = TRACK_W): number => {
    if (max <= 1) {
        return 0
    }
    return ((clampQuantity(quantity, max) - 1) / (max - 1)) * trackW
}

export const quantityAtOffset = (offset: number, max: number, trackW: number = TRACK_W): number => {
    if (max <= 1 || trackW <= 0) {
        return 1
    }
    const ratio = Math.max(0, Math.min(1, offset / trackW))
    return clampQuantity(1 + Math.round(ratio * (max - 1)), max)
}

// ---- typed entry ------------------------------------------------------------

// Append a digit. Overshooting the stack pins to max rather than rejecting the keystroke, so typing
// "99" into a stack of 34 reads as "take the lot" instead of silently dropping the second 9.
export const applyDigit = (buffer: string, digit: string, max: number): string => {
    if (!/^[0-9]$/.test(digit)) {
        return buffer
    }
    const next = `${buffer}${digit}`.replace(/^0+(?=\d)/, "")
    return Number(next) > max ? String(clampQuantity(max, max)) : next
}

export const applyBackspace = (buffer: string): string => buffer.slice(0, -1)

// What a buffer means as an amount. An empty buffer (or a bare "0") is a half-typed number: the
// picker shows it as typed but has nothing to transfer yet, so confirm stays disabled.
export const bufferQuantity = (buffer: string, max: number): number | undefined => {
    if (!/^[0-9]+$/.test(buffer)) {
        return undefined
    }
    const value = Number(buffer)
    return value < 1 ? undefined : clampQuantity(value, max)
}

export const halfOf = (max: number): number => Math.max(1, Math.floor(max / 2))
